import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Package, Users } from 'lucide-react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

const roleLabel = (r) => (r || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export const CopyBranchSetupDialog = ({ branch, onClose, onDone }) => {
  const [preview, setPreview] = useState(null);
  const [products, setProducts] = useState(true);
  const [staff, setStaff] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/branches/${branch.tenant_id}/copy-preview`).then(({ data }) => {
      setPreview(data);
      setStaff(data.staff.filter(s => s.has_access).map(s => s.user_id));
    }).catch(e => { toast.error(e?.response?.data?.detail || 'Could not load the head branch setup'); onClose(); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch.tenant_id]);

  const toggle = (id) => setStaff(list => (list.includes(id) ? list.filter(x => x !== id) : [...list, id]));

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/branches/${branch.tenant_id}/copy-setup`, { copy_products: products, staff_user_ids: staff });
      toast.success(`Copied ${data.products_copied} product${data.products_copied === 1 ? '' : 's'}`
        + (data.products_skipped ? ` (${data.products_skipped} already there)` : '')
        + ` · ${data.staff_with_access} staff can work here`);
      onDone();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not copy the setup'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="copy-setup-dialog">
        <DialogHeader>
          <DialogTitle>Copy head-branch setup to {branch.name}</DialogTitle>
          <DialogDescription>Bring across what you already set up at your head location in one go.</DialogDescription>
        </DialogHeader>
        {!preview ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> : (
          <div className="space-y-5 py-1 text-sm">
            <label className="flex items-start gap-3 rounded-md border p-3 cursor-pointer" data-testid="copy-setup-products">
              <Checkbox checked={products} onCheckedChange={v => setProducts(!!v)} data-testid="copy-setup-products-checkbox" />
              <span>
                <span className="flex items-center gap-1.5 font-medium"><Package className="h-4 w-4 text-primary" /> Products and selling prices</span>
                <span className="block text-xs text-muted-foreground" data-testid="copy-setup-products-note">
                  {preview.head_products} products at the head branch{preview.branch_products ? `; ${preview.branch_products} already in this branch` : ''}. Stock starts at zero and batches are not copied; products already in this branch are skipped.
                </span>
              </span>
            </label>
            <div className="space-y-2" data-testid="copy-setup-staff">
              <p className="flex items-center gap-1.5 font-medium"><Users className="h-4 w-4 text-primary" /> Head-branch staff who can also work here</p>
              <p className="text-xs text-muted-foreground">They keep their role and any custom permissions, and switch branch from the top bar.</p>
              {preview.staff.length === 0 ? <p className="text-xs text-muted-foreground" data-testid="copy-setup-no-staff">No head-branch staff yet.</p> : (
                <div className="max-h-56 overflow-y-auto rounded-md border divide-y">
                  {preview.staff.map(s => (
                    <label key={s.user_id} className="flex items-center gap-3 px-3 py-2 cursor-pointer" data-testid={`copy-setup-staff-${s.user_id}`}>
                      <Checkbox checked={staff.includes(s.user_id)} onCheckedChange={() => toggle(s.user_id)} data-testid={`copy-setup-staff-checkbox-${s.user_id}`} />
                      <span className="flex-1 truncate">{s.name}</span>
                      <span className="text-xs text-muted-foreground">{roleLabel(s.role)}{s.custom_permissions ? ` · ${s.custom_permissions} custom` : ''}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="copy-setup-cancel">Cancel</Button>
          <Button onClick={run} disabled={busy || !preview} data-testid="copy-setup-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Copy setup
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
